import { useState } from 'react';
import { createTheme, MantineProvider } from '@mantine/core';
import { Card, Stack, Text, Button, Group } from '@mantine/core';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import '@mantine/core/styles.layer.css';

const theme = createTheme({});

interface ErrorFallbackProps {
  error: Error | null;
  resetError: () => void;
}

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  const errorMessage =
    error && typeof error === 'object' && 'message' in error
      ? String(error.message)
      : 'Si è verificato un errore imprevisto';

  const handleRetry = () => {
    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      // After max attempts, reload the page
      window.location.reload();
      return;
    }

    setIsRetrying(true);
    setRetryCount(prev => prev + 1);

    // Add a delay before retrying to prevent immediate infinite loops
    setTimeout(() => {
      try {
        resetError();
      } catch (e) {
        console.error('Error during reset:', e);
      } finally {
        setIsRetrying(false);
      }
    }, RETRY_DELAY_MS);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <MantineProvider theme={theme}>
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          backgroundColor: 'var(--mantine-color-gray-0)',
        }}
      >
        <Card
          shadow='md'
          padding='xl'
          radius='md'
          withBorder
          style={{ maxWidth: '600px', width: '100%' }}
        >
          <Stack gap='md' align='center'>
            <AlertCircle size={48} color='var(--mantine-color-red-6)' />

            <Stack gap='xs' align='center'>
              <Text size='xl' fw={600} c='red'>
                Qualcosa è andato storto
              </Text>
              <Text size='sm' c='dimmed' ta='center'>
                {errorMessage}
              </Text>
            </Stack>

            {retryCount >= MAX_RETRY_ATTEMPTS && (
              <Card
                padding='sm'
                radius='sm'
                style={{
                  backgroundColor: 'var(--mantine-color-yellow-0)',
                  border: '1px solid var(--mantine-color-yellow-3)',
                  width: '100%',
                }}
              >
                <Text size='xs' c='yellow.9' ta='center'>
                  Sono stati effettuati troppi tentativi. Si consiglia di
                  ricaricare la pagina.
                </Text>
              </Card>
            )}

            <Group gap='sm' mt='md'>
              {retryCount < MAX_RETRY_ATTEMPTS && (
                <Button
                  onClick={handleRetry}
                  loading={isRetrying}
                  leftSection={<RefreshCw size={16} />}
                  variant='light'
                >
                  Riprova
                </Button>
              )}
              <Button
                onClick={handleReload}
                leftSection={<RefreshCw size={16} />}
                variant='filled'
              >
                Ricarica pagina
              </Button>
              <Button
                onClick={handleGoHome}
                leftSection={<Home size={16} />}
                variant='subtle'
              >
                Torna alla home
              </Button>
            </Group>

            {retryCount > 0 && retryCount < MAX_RETRY_ATTEMPTS && (
              <Text size='xs' c='dimmed'>
                Tentativo {retryCount} di {MAX_RETRY_ATTEMPTS}
              </Text>
            )}
          </Stack>
        </Card>
      </div>
    </MantineProvider>
  );
}
